// InfoTooltip.tsx
import * as React from 'react';
import { Box, Tooltip, Typography, type SxProps } from '@mui/material';
import type { ReactNode } from 'react';
import ToolTipIcon from '../../assets/PepWise2.0/material-symbols-light_info-outline.png';
import { useTheme } from "@mui/material/styles";

export type TooltipItem = { title: string; description: string };
export type TooltipLabel = string | React.ReactNode | TooltipItem[];

interface InfoTooltipProps {
	label?: TooltipLabel;
	icon?: ReactNode;
	cursor?: React.CSSProperties["cursor"];
	className?: string;
	sx?: SxProps;
	listVariant?: React.ComponentProps<typeof Typography>['variant'];
	tooltipProps?: Partial<React.ComponentProps<typeof Tooltip>>;
	hasNoCursor?: boolean;
	tooltipBackgroundColor?: string;
	tooltipTextColor?: string;
}

const isTooltipItemArray = (x: unknown): x is TooltipItem[] =>
	Array.isArray(x) && x.every((i) => typeof i?.title === 'string');

export const InfoTooltip = React.memo(function InfoTooltip({
	label,
	icon,
	cursor = "help",
	className,
	sx,
	listVariant = 'body2',
	tooltipProps,
	hasNoCursor = false,
	tooltipBackgroundColor,
	tooltipTextColor
}: InfoTooltipProps) {
	const theme= useTheme();
	const colors = theme.palette;

	const content = React.useMemo(() => {
		let result: React.ReactNode;

		if (React.isValidElement(label)) {
			result = label;
		} else {
			// allow label to be a render function
			const labelTxt =
			typeof label === 'function'
				? (label as () => React.ReactNode)()
				: label;

			if (React.isValidElement(labelTxt)) {
			result = labelTxt;
			} else if (isTooltipItemArray(labelTxt)) {
			result = (
				<Box
				component="ul"
				sx={{
					m: 0,
					p: 0,
					listStyle: 'none',
				}}
				>
				{labelTxt.map((item, idx) => (
					<Box
					component="li"
					key={`${item.title}-${idx}`}
					sx={{
						backgroundColor: colors.tooltip.background.primary,
						m: 0,
						p: 1,
					}}
					>
					<Typography variant={listVariant} component="div" sx={{ m: 0 }}>
						<Box component="strong" sx={{ color: colors.tooltip.text.title }}>
						{item.title}:{' '}
						</Box>
						<Box
						component="span"
						sx={{ color: colors.tooltip.text.description }}
						>
						{item.description}
						</Box>
					</Typography>
					</Box>
				))}
				</Box>
			);
			} else {
			result = (
				<Box
				sx={{
					backgroundColor: tooltipBackgroundColor || colors.tooltip.background.primary,
					m: 0,
					p: 1,
				}}
				>
				<Typography
					variant={listVariant}
					sx={{ m: 0, color: tooltipTextColor || colors.tooltip.text.title }}
				>
					{labelTxt as React.ReactNode}
				</Typography>
				</Box>
			);
			}
		}

		return result;
		}, [label, listVariant, theme]);

	if (!label) return null;

	const resolvedCursor = hasNoCursor ? "default" : cursor;

	const trigger = icon ? (
		<Box sx={{ cursor: resolvedCursor, lineHeight: 0, ml: 1 }}>{icon}</Box>
	) : (
		<Box sx={{ cursor: resolvedCursor, lineHeight: 0, ml: 1 }}>
			<img src={ToolTipIcon} alt="info" />
		</Box>
	);

	return (
		<Box
			className={className}
			sx={{
				transition: 'opacity 0.3s ease-in-out',
				display: 'flex',
				alignItems: 'center',
				...sx
			}}
		>
			<Tooltip
				title={content}
				{...tooltipProps}
				slotProps={{
					tooltip: {
						sx: {
							p: 0, // remove default tooltip padding
							bgcolor: colors.tooltip.background.primary,
							color: colors.tooltip.text.primary,
							boxShadow: 2,
							borderRadius: 1,
							...(tooltipProps?.slotProps?.tooltip?.sx as any)
						}
					}
				}}
			>
				{trigger}
			</Tooltip>
		</Box>
	);
});
